import React, { useState, useRef, useEffect } from "react";
import { formatDistanceToNow } from "date-fns";
import { lt, enUS } from "date-fns/locale";
import { PostComment, Reaction } from "types/Post";
import { useSelector } from "react-redux";
import { RootState } from "configureStore";
import { fetchComments, toggleCommentReaction } from "services/posts.service";
import { useTranslation } from "react-i18next";
import { truncateComment, isCommentTruncated, formatDate, withCommentReactions } from "../utils/commentUtils";
import { toggleReactionOptimistically } from "../utils/reactions";
import { ReactionPills } from "./ReactionPills";
import { UserNameWithCompany } from "./UserNameWithCompany";
import { TOUCH_BUTTON_ROW } from "./touchTargets";

const COMMENT_CHAR_LIMIT = 300;

// The row of small text buttons under a comment wraps instead of running off a narrow column.
const COMMENT_ACTIONS_ROW = `mt-1 ml-1 flex flex-wrap items-center gap-x-2 gap-y-1 ${TOUCH_BUTTON_ROW}`;

interface CommentsSectionProps {
  postId: string;
  companyId: string;
  initialComments: PostComment[];
  initialCommentsCount: number;
  onComment: (postId: string, comment: string, parent?: string) => Promise<PostComment>;
  onDeleteComment?: (postId: string, commentId: string) => Promise<void>;
  onUpdateComment?: (postId: string, commentId: string, comment: string) => Promise<PostComment>;
  onCommentsCountChange?: (count: number) => void;
  /** Comments written from another company than the post's get a company label. */
  postCompanyId: string;
}

export function CommentsSection({
  postId,
  companyId,
  initialComments,
  initialCommentsCount,
  onComment,
  onDeleteComment,
  onUpdateComment,
  onCommentsCountChange,
  postCompanyId,
}: CommentsSectionProps) {
  const { t, i18n } = useTranslation();
  const { session } = useSelector((state: RootState) => state.authReducer);
  
  const [comments, setComments] = useState<PostComment[]>(() => {
    return Array.isArray(initialComments) ? initialComments : [];
  });
  const [commentsCount, setCommentsCount] = useState(initialCommentsCount);
  const [commentText, setCommentText] = useState("");
  const [commentError, setCommentError] = useState("");
  const [replyErrors, setReplyErrors] = useState<{ [key: string]: string }>({});
  const [editErrors, setEditErrors] = useState<{ [key: string]: string }>({});
  const [isCommenting, setIsCommenting] = useState(false);
  // A reaction is on its way; the pills of all comments wait for its answer.
  const [isReacting, setIsReacting] = useState(false);
  const [reactionError, setReactionError] = useState<string | null>(null);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [hasMoreComments, setHasMoreComments] = useState(true);
  const [isLoadingMoreComments, setIsLoadingMoreComments] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState<{ [key: string]: string }>({});
  const [isReplying, setIsReplying] = useState<{ [key: string]: boolean }>({});
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentText, setEditCommentText] = useState<{ [key: string]: string }>({});
  const [isUpdatingComment, setIsUpdatingComment] = useState<{ [key: string]: boolean }>({});
  const [collapsedReplies, setCollapsedReplies] = useState<Set<string>>(new Set());
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const [expandedReplyComments, setExpandedReplyComments] = useState<Set<string>>(new Set());
  // The comment whose «Delete» was tapped and now waits for «Confirm» or «Cancel».
  const [deleteCandidateId, setDeleteCandidateId] = useState<string | null>(null);
  
  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  const replyInputRefs = useRef<{ [key: string]: HTMLTextAreaElement | null }>({});
  const editInputRefs = useRef<{ [key: string]: HTMLTextAreaElement | null }>({});
  const hasLoadedRef = useRef(false);

  // Update comments count callback
  useEffect(() => {
    if (onCommentsCountChange) {
      onCommentsCountChange(commentsCount);
    }
  }, [commentsCount, onCommentsCountChange]);

  // Load comments when component mounts
  useEffect(() => {
    const loadComments = async () => {
      if (hasLoadedRef.current || isLoadingComments) return;

      hasLoadedRef.current = true;
      setIsLoadingComments(true);
      try {
        const fetchedComments = await fetchComments(companyId, postId);
        const commentsArray = Array.isArray(fetchedComments) ? fetchedComments : [];
        setComments(commentsArray);
        setCommentsLoaded(true);

        // Check if there are more comments
        if (commentsArray.length >= commentsCount) {
          setHasMoreComments(false);
        }
      } catch (error) {
        console.error("Error loading comments:", error);
        hasLoadedRef.current = false; // Reset on error to allow retry
      } finally {
        setIsLoadingComments(false);
      }
    };

    loadComments();
  }, [companyId, postId, commentsCount]);

  // Load more comments
  const loadMoreComments = async () => {
    if (isLoadingMoreComments || !hasMoreComments) return;
    
    setIsLoadingMoreComments(true);
    try {
      const fetchedComments = await fetchComments(companyId, postId);
      const commentsArray = Array.isArray(fetchedComments) ? fetchedComments : [];
      
      setComments((prev) => {
        const existingIds = new Set(prev.map(c => c.id));
        const newComments = commentsArray.filter(c => !existingIds.has(c.id));
        const updatedComments = [...prev, ...newComments];
        
        const totalLoaded = updatedComments.length;
        if (totalLoaded >= commentsCount) {
          setHasMoreComments(false);
        }
        
        return updatedComments;
      });
    } catch (error) {
      console.error("Error loading more comments:", error);
    } finally {
      setIsLoadingMoreComments(false);
    }
  };

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim() || isCommenting) return;

    if (commentText.length > COMMENT_CHAR_LIMIT) {
      setCommentError(t("feed_comment_length_error", { limit: COMMENT_CHAR_LIMIT, current: commentText.length }));
      return;
    }

    setCommentError("");
    setIsCommenting(true);
    try {
      const newComment = await onComment(postId, commentText);
      setCommentText("");
      setCommentsCount((prev) => prev + 1);
      setComments((prev) => {
        const prevArray = Array.isArray(prev) ? prev : [];
        return [...prevArray, newComment];
      });
    } catch (error) {
      console.error("Error adding comment:", error);
    } finally {
      setIsCommenting(false);
    }
  };

  const findCommentDepth = (comments: PostComment[], targetId: string, currentDepth: number = 0): number | null => {
    for (const comment of comments) {
      if (comment.id === targetId) {
        return currentDepth;
      }
      if (comment.replies && comment.replies.length > 0) {
        const depth = findCommentDepth(comment.replies, targetId, currentDepth + 1);
        if (depth !== null) return depth;
      }
    }
    return null;
  };

  const handleReplySubmit = async (parentCommentId: string) => {
    const replyTextValue = replyText[parentCommentId];
    if (!replyTextValue?.trim() || isReplying[parentCommentId]) return;

    if (replyTextValue.length > COMMENT_CHAR_LIMIT) {
      setReplyErrors((prev) => ({
        ...prev,
        [parentCommentId]: t("feed_reply_length_error", { limit: COMMENT_CHAR_LIMIT, current: replyTextValue.length })
      }));
      return;
    }

    setReplyErrors((prev) => ({ ...prev, [parentCommentId]: "" }));

    // Prevent nesting deeper than 1 level (depth 0 = top level, depth 1 = first reply)
    const parentDepth = findCommentDepth(comments, parentCommentId);
    if (parentDepth !== null && parentDepth > 0) {
      // Don't allow replies to replies (only allow one level of nesting)
      return;
    }

    setIsReplying((prev) => ({ ...prev, [parentCommentId]: true }));
    try {
      const newReply = await onComment(postId, replyTextValue, parentCommentId);
      setReplyText((prev) => ({ ...prev, [parentCommentId]: "" }));
      setReplyingTo(null);
      setCommentsCount((prev) => prev + 1);
      
      const currentExpandedComments = expandedComments;
      setIsLoadingComments(true);
      try {
        const fetchedComments = await fetchComments(companyId, postId);
        const commentsArray = Array.isArray(fetchedComments) ? fetchedComments : [];
        setComments(commentsArray);
        
        setExpandedComments((prev) => {
          const newSet = new Set(currentExpandedComments);
          newSet.add(parentCommentId);
          return newSet;
        });
      } catch (error) {
      } finally {
        setIsLoadingComments(false);
      }
    } catch (error) {
      console.error("Error adding reply:", error);
    } finally {
      setIsReplying((prev) => ({ ...prev, [parentCommentId]: false }));
    }
  };

  const handleReplyClick = (commentId: string) => {
    setReplyingTo(commentId);
    setReplyText((prev) => ({ ...prev, [commentId]: "" }));
    setReplyErrors((prev) => ({ ...prev, [commentId]: "" }));
    
    setTimeout(() => {
      const input = replyInputRefs.current[commentId];
      if (input) {
        input.focus();
      }
    }, 100);
  };

  const organizeComments = (commentsList: PostComment[]): PostComment[] => {
    const parentComments: PostComment[] = [];
    const replyMap: { [key: string]: PostComment[] } = {};

    commentsList.forEach((comment) => {
      if (!comment.parent) {
        parentComments.push(comment);
      } else {
        if (!replyMap[comment.parent]) {
          replyMap[comment.parent] = [];
        }
        replyMap[comment.parent].push(comment);
      }
    });

    const attachReplies = (comment: PostComment): PostComment => {
      const replies = replyMap[comment.id] || [];
      return {
        ...comment,
        replies: replies.map(attachReplies),
      };
    };

    return parentComments.map(attachReplies);
  };

  const getAllChildIdsFromNested = (comment: PostComment): string[] => {
    let ids = [comment.id];
    if (comment.replies && comment.replies.length > 0) {
      comment.replies.forEach((reply) => {
        ids = ids.concat(getAllChildIdsFromNested(reply));
      });
    }
    return ids;
  };

  const getAllChildIds = (commentsList: PostComment[], parentId: string): string[] => {
    const parentComment = commentsList.find((c) => c.id === parentId);
    if (!parentComment) return [];
    return getAllChildIdsFromNested(parentComment);
  };

  const findCommentAndGetChildren = (commentsList: PostComment[], targetId: string): string[] => {
    for (const comment of commentsList) {
      if (comment.id === targetId) {
        return getAllChildIdsFromNested(comment);
      }
      if (comment.replies && comment.replies.length > 0) {
        const childIds = findCommentAndGetChildren(comment.replies, targetId);
        if (childIds.length > 0) {
          return [comment.id, ...childIds];
        }
      }
    }
    return [];
  };

  const handleEditComment = (commentId: string, currentText: string) => {
    setEditingCommentId(commentId);
    setEditCommentText((prev) => ({ ...prev, [commentId]: currentText }));
    setEditErrors((prev) => ({ ...prev, [commentId]: "" }));
    
    setTimeout(() => {
      const input = editInputRefs.current[commentId];
      if (input) {
        input.focus();
        input.setSelectionRange(input.value.length, input.value.length);
      }
    }, 100);
  };

  const handleCancelEdit = (commentId: string) => {
    setEditingCommentId(null);
    setEditCommentText((prev) => {
      const newText = { ...prev };
      delete newText[commentId];
      return newText;
    });
    setEditErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[commentId];
      return newErrors;
    });
  };

  const updateCommentInList = (commentsList: PostComment[], commentId: string, newComment: string, updatedAt?: number): PostComment[] => {
    return commentsList.map((comment) => {
      if (comment.id === commentId) {
        return {
          ...comment,
          comment: newComment,
          updatedAt: updatedAt || comment.updatedAt,
        };
      }
      if (comment.replies && comment.replies.length > 0) {
        return {
          ...comment,
          replies: updateCommentInList(comment.replies, commentId, newComment, updatedAt),
        };
      }
      return comment;
    });
  };

  const handleUpdateComment = async (commentId: string) => {
    const editText = editCommentText[commentId];
    if (!editText?.trim() || isUpdatingComment[commentId] || !onUpdateComment) return;

    if (editText.length > COMMENT_CHAR_LIMIT) {
      setEditErrors((prev) => ({
        ...prev,
        [commentId]: t("feed_comment_length_error", { limit: COMMENT_CHAR_LIMIT, current: editText.length })
      }));
      return;
    }

    setIsUpdatingComment((prev) => ({ ...prev, [commentId]: true }));
    setEditErrors((prev) => ({ ...prev, [commentId]: "" }));

    try {
      const updatedComment = await onUpdateComment(postId, commentId, editText);
      setComments((prev) => updateCommentInList(prev, commentId, editText, updatedComment.updatedAt));
      handleCancelEdit(commentId);
    } catch (error) {
      console.error("Error updating comment:", error);
      setEditErrors((prev) => ({
        ...prev,
        [commentId]: t("feed_failed_to_update_comment") || "Failed to update comment"
      }));
    } finally {
      setIsUpdatingComment((prev) => ({ ...prev, [commentId]: false }));
    }
  };

  // The pressed reaction shows at once; the server's counts replace it, and a failure puts the old ones back.
  const handleReact = async (comment: PostComment, reaction: Reaction) => {
    setIsReacting(true);
    setReactionError(null);
    try {
      await toggleReactionOptimistically({
        current: comment.reactions,
        reaction,
        show: (reactions) => setComments((prev) => withCommentReactions(prev, comment.id, reactions)),
        send: () => toggleCommentReaction(companyId, postId, comment.id, reaction),
      });
    } catch (error) {
      console.error("Error toggling comment reaction:", error);
      setReactionError(t("news_reaction_failed"));
    } finally {
      setIsReacting(false);
    }
  };

  const removeCommentFromNested = (commentsList: PostComment[], targetId: string): PostComment[] => {
    return commentsList
      .filter((comment) => comment.id !== targetId)
      .map((comment) => {
        if (comment.replies && comment.replies.length > 0) {
          return {
            ...comment,
            replies: removeCommentFromNested(comment.replies, targetId),
          };
        }
        return comment;
      });
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!onDeleteComment) return;
    
    try {
      await onDeleteComment(postId, commentId);
      setComments((prev) => removeCommentFromNested(prev, commentId));
      setCommentsCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  // «Delete» sits next to «Edit» under the comment, so it takes a second tap to delete.
  const renderDeleteAction = (commentId: string) =>
    deleteCandidateId === commentId ? (
      <>
        <button
          onClick={() => {
            setDeleteCandidateId(null);
            handleDeleteComment(commentId);
          }}
          className="text-xs text-red-600 hover:text-red-700 font-medium"
        >
          {t("feed_confirm_delete")}
        </button>
        <button
          onClick={() => setDeleteCandidateId(null)}
          className="text-xs text-gray-500 hover:text-gray-700 font-medium"
        >
          {t("feed_cancel")}
        </button>
      </>
    ) : (
      <button
        onClick={() => setDeleteCandidateId(commentId)}
        className="text-xs text-gray-500 hover:text-red-600 font-medium"
      >
        {t("feed_delete")}
      </button>
    );

  const renderNestedReplies = (replies: PostComment[], depth: number = 0, maxDepth: number = 1): React.ReactNode => {
    if (!replies || replies.length === 0) return null;

    const shouldCollapse = depth >= maxDepth;

    return (
      <div className={`mt-2 space-y-2 ${depth < maxDepth ? 'ml-4 border-l-2 border-gray-200 pl-3 max-md:ml-0 max-md:pl-2' : 'ml-2 max-md:ml-0'}`}>
        {replies.map((reply) => {
          const isOwnReply = session?.id === reply.user.id;
          const replyUserName = reply.user.name || t("feed_user");
          const replyUserInitial = replyUserName.charAt(0).toUpperCase();
          const replyDisplayName = (
            <UserNameWithCompany
              name={replyUserName}
              companyName={reply.companyName}
              companyId={reply.company}
              ownCompanyId={postCompanyId}
            />
          );
          const hasNestedReplies = reply.replies && reply.replies.length > 0;
          const isCollapsed = collapsedReplies.has(reply.id);
          const shouldShowCompact = shouldCollapse && isCollapsed;

          if (shouldShowCompact) {
            return (
              <div key={reply.id} className="flex items-start space-x-2 py-1">
                <div className="w-5 h-5 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0 mt-0.5">
                  {replyUserInitial}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center flex-wrap gap-1 text-sm">
                    <span className="font-semibold text-gray-900">{replyDisplayName}</span>
                    <span className="text-gray-700 break-words">
                      {expandedReplyComments.has(reply.id) 
                        ? reply.comment 
                        : truncateComment(reply.comment)}
                    </span>
                  </div>
                  <div className={`flex items-center flex-wrap gap-x-2 gap-y-1 mt-0.5 ${TOUCH_BUTTON_ROW}`}>
                    {isCommentTruncated(reply.comment) && !expandedReplyComments.has(reply.id) && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedReplyComments((prev) => {
                              const newSet = new Set(prev);
                              newSet.add(reply.id);
                              return newSet;
                            });
                          }}
                          className="text-blue-600 hover:text-blue-700 font-medium text-xs"
                        >
                          {t("feed_show_more_capital")}
                        </button>
                        <span className="text-gray-500 text-xs">·</span>
                      </>
                    )}
                    {expandedReplyComments.has(reply.id) && isCommentTruncated(reply.comment) && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedReplyComments((prev) => {
                              const newSet = new Set(prev);
                              newSet.delete(reply.id);
                              return newSet;
                            });
                          }}
                          className="text-blue-600 hover:text-blue-700 font-medium text-xs"
                        >
                          {t("feed_show_less_capital")}
                        </button>
                        <span className="text-gray-500 text-xs">·</span>
                      </>
                    )}
                    <span className="text-gray-500 text-xs">{formatDate(reply.createdAt, i18n, t)}</span>
                    {hasNestedReplies && (
                      <>
                        <span className="text-gray-500 text-xs">·</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCollapsedReplies((prev) => {
                              const newSet = new Set(prev);
                              newSet.delete(reply.id);
                              return newSet;
                            });
                          }}
                          className="text-blue-600 hover:text-blue-700 font-medium text-xs"
                        >
                          {(reply.replies?.length || 0) === 1 
                            ? t("feed_view_reply_singular", { count: reply.replies?.length || 0 })
                            : t("feed_view_replies_plural", { count: reply.replies?.length || 0 })}
                        </button>
                      </>
                    )}
                    {isOwnReply && onDeleteComment && (
                      <>
                        <span className="text-gray-500 text-xs">·</span>
                        {renderDeleteAction(reply.id)}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div key={reply.id} className="space-y-2">
              <div className="flex items-start space-x-2 group">
                <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                  {replyUserInitial}
                </div>
                <div className="flex-1 min-w-0" data-testid="comment">
                  {editingCommentId === reply.id ? (
                    <div className="bg-gray-100 rounded-2xl px-3 py-2 inline-block max-w-full">
                      <div className="flex items-baseline space-x-2">
                        <span className="font-semibold text-sm text-gray-900">
                          {replyDisplayName}
                        </span>
                        <span className="text-sm text-gray-500 italic">
                          ({t("feed_editing")})
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-100 rounded-2xl px-3 py-2">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-start justify-between gap-2">
                          <span className="font-semibold text-sm text-gray-900">
                            {replyDisplayName}
                          </span>
                          {reply.updatedAt && reply.updatedAt > reply.createdAt && (
                            <span className="text-xs text-gray-500 italic whitespace-nowrap flex-shrink-0">
                              {t("feed_edited")}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          <span className="text-sm text-gray-700 break-words break-all">
                            {expandedReplyComments.has(reply.id) 
                              ? reply.comment 
                              : truncateComment(reply.comment)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className={COMMENT_ACTIONS_ROW}>
                    {isCommentTruncated(reply.comment) && !expandedReplyComments.has(reply.id) && (
                      <>
                        <button
                          onClick={() => setExpandedReplyComments((prev) => {
                            const newSet = new Set(prev);
                            newSet.add(reply.id);
                            return newSet;
                          })}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                        >
                          {t("feed_show_more_capital")}
                        </button>
                        <span className="text-xs text-gray-500">·</span>
                      </>
                    )}
                    {expandedReplyComments.has(reply.id) && isCommentTruncated(reply.comment) && (
                      <>
                        <button
                          onClick={() => setExpandedReplyComments((prev) => {
                            const newSet = new Set(prev);
                            newSet.delete(reply.id);
                            return newSet;
                          })}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                        >
                          {t("feed_show_less_capital")}
                        </button>
                        <span className="text-xs text-gray-500">·</span>
                      </>
                    )}
                    <span className="text-xs text-gray-500">
                      {formatDate(reply.createdAt, i18n, t)}
                    </span>
                    {isOwnReply && onUpdateComment && (
                      <>
                        <span className="text-xs text-gray-500">·</span>
                        <button
                          onClick={() => handleEditComment(reply.id, reply.comment)}
                          className="text-xs text-gray-500 hover:text-blue-600 font-medium"
                        >
                          {t("feed_edit")}
                        </button>
                      </>
                    )}
                    {isOwnReply && onDeleteComment && (
                      <>
                        <span className="text-xs text-gray-500">·</span>
                        {renderDeleteAction(reply.id)}
                      </>
                    )}
                  </div>
                  <div className="mt-1 ml-1">
                    <ReactionPills
                      reactions={reply.reactions}
                      onToggle={(reaction) => handleReact(reply, reaction)}
                      isDisabled={isReacting}
                      isSmall
                    />
                  </div>

                  {editingCommentId === reply.id && (
                    <div className="mt-2 ml-1 flex items-start space-x-2">
                      <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                        {replyUserInitial}
                      </div>
                      <div className="flex-1">
                        <div className="bg-gray-100 rounded-2xl px-3 py-2 focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                          <textarea
                            ref={(el) => {
                              if (el) editInputRefs.current[reply.id] = el;
                            }}
                            value={editCommentText[reply.id] || ""}
                            onChange={(e) =>
                              setEditCommentText((prev) => ({
                                ...prev,
                                [reply.id]: e.target.value,
                              }))
                            }
                            onKeyPress={(e) => {
                              if (e.key === "Enter" && e.ctrlKey) {
                                e.preventDefault();
                                handleUpdateComment(reply.id);
                              }
                              if (e.key === "Escape") {
                                handleCancelEdit(reply.id);
                              }
                            }}
                            placeholder={t("feed_edit_comment_max", { limit: COMMENT_CHAR_LIMIT })}
                            className="w-full bg-transparent border-none outline-none text-base md:text-sm text-gray-900 placeholder-gray-500 resize-none min-h-[80px]"
                            disabled={isUpdatingComment[reply.id]}
                            rows={3}
                          />
                        </div>
                        {editErrors[reply.id] && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-xs text-red-600 font-medium">{editErrors[reply.id]}</p>
                          </div>
                        )}
                        {editingCommentId === reply.id && (
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-gray-500">
                              {editCommentText[reply.id]?.length || 0} / {COMMENT_CHAR_LIMIT}
                            </span>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleCancelEdit(reply.id)}
                                className="text-sm text-gray-600 hover:text-gray-800 font-medium px-3 py-1"
                              >
                                {t("feed_cancel")}
                              </button>
                              <button
                                onClick={() => handleUpdateComment(reply.id)}
                                disabled={isUpdatingComment[reply.id] || ((editCommentText[reply.id]?.length || 0) > COMMENT_CHAR_LIMIT)}
                                className="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
                              >
                                {isUpdatingComment[reply.id] ? t("feed_updating") : t("feed_save")}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const userInitial = (session?.email || t("feed_user")).charAt(0).toUpperCase();

  return (
    <div className="mt-4 pt-4 border-t border-gray-200">
      {reactionError && (
        <p role="alert" className="mb-2 text-xs font-medium text-red-600">
          {reactionError}
        </p>
      )}
      {/* Comments List */}
      {isLoadingComments ? (
        <div className="space-y-3 mb-4">
          {[1, 2].map((i) => (
            <div key={i} className="flex items-start space-x-2 animate-pulse">
              <div className="w-8 h-8 bg-gray-200 rounded-full flex-shrink-0"></div>
              <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2">
                <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-full"></div>
              </div>
            </div>
          ))}
        </div>
      ) : comments.length > 0 ? (
        <div className={`space-y-3 mb-4 ${comments.length > 3 ? 'max-h-[300px] overflow-y-auto overflow-x-hidden pr-2' : ''}`}>
          {organizeComments(comments).map((comment) => {
            const isOwnComment = session?.id === comment.user.id;
            const commentUserName = comment.user.name || t("feed_user");
            const commentUserInitial = commentUserName.charAt(0).toUpperCase();
            const displayName = (
              <UserNameWithCompany
                name={commentUserName}
                companyName={comment.companyName}
                companyId={comment.company}
                ownCompanyId={postCompanyId}
              />
            );

            return (
              <div key={comment.id} className="space-y-2">
                {/* Parent Comment */}
                <div className="flex items-start space-x-2 group">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                    {commentUserInitial}
                  </div>
                  <div className="flex-1 min-w-0" data-testid="comment">
                    <div className="bg-gray-100 rounded-2xl px-3 py-2">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-start justify-between gap-2">
                          <span className="font-semibold text-sm text-gray-900">
                            {displayName}
                          </span>
                          {comment.updatedAt && comment.updatedAt > comment.createdAt && (
                            <span className="text-xs text-gray-500 italic whitespace-nowrap flex-shrink-0">
                              {t("feed_edited")}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          <span className="text-sm text-gray-700 break-words break-all">
                            {expandedComments.has(comment.id) 
                              ? comment.comment 
                              : truncateComment(comment.comment)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className={COMMENT_ACTIONS_ROW}>
                      {isCommentTruncated(comment.comment) && !expandedComments.has(comment.id) && (
                        <>
                          <button
                            onClick={() => setExpandedComments((prev) => {
                              const newSet = new Set(prev);
                              newSet.add(comment.id);
                              return newSet;
                            })}
                            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                          >
                            {t("feed_show_more_capital")}
                          </button>
                          <span className="text-xs text-gray-500">·</span>
                        </>
                      )}
                      {expandedComments.has(comment.id) && isCommentTruncated(comment.comment) && (
                        <>
                          <button
                            onClick={() => setExpandedComments((prev) => {
                              const newSet = new Set(prev);
                              newSet.delete(comment.id);
                              return newSet;
                            })}
                            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                          >
                            {t("feed_show_less_capital")}
                          </button>
                          <span className="text-xs text-gray-500">·</span>
                        </>
                      )}
                      
                      <button
                        onClick={() => handleReplyClick(comment.id)}
                        className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                      >
                        {t("feed_reply")}
                      </button>
                      <span className="text-xs text-gray-500">·</span>
                      <span className="text-xs text-gray-500">
                        {formatDate(comment.createdAt, i18n, t)}
                      </span>
                      {isOwnComment && (
                        <>
                          <span className="text-xs text-gray-500">·</span>
                          {onUpdateComment && (
                            <>
                              <button
                                onClick={() => handleEditComment(comment.id, comment.comment)}
                                className="text-xs text-gray-500 hover:text-blue-600 font-medium"
                              >
                                {t("feed_edit")}
                              </button>
                              <span className="text-xs text-gray-500">·</span>
                            </>
                          )}
                          {onDeleteComment && renderDeleteAction(comment.id)}
                        </>
                      )}
                    </div>
                    <div className="mt-1 ml-1">
                      <ReactionPills
                        reactions={comment.reactions}
                        onToggle={(reaction) => handleReact(comment, reaction)}
                        isDisabled={isReacting}
                        isSmall
                      />
                    </div>

                    {/* Edit Input for top-level comments */}
                    {editingCommentId === comment.id && (
                      <div className="mt-2 ml-1 flex items-start space-x-2">
                        <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                          {commentUserInitial}
                        </div>
                        <div className="flex-1">
                          <div className="bg-gray-100 rounded-2xl px-3 py-2 focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                            <textarea
                              ref={(el) => {
                                if (el) editInputRefs.current[comment.id] = el;
                              }}
                              value={editCommentText[comment.id] || ""}
                              onChange={(e) =>
                                setEditCommentText((prev) => ({
                                  ...prev,
                                  [comment.id]: e.target.value,
                                }))
                              }
                              onKeyPress={(e) => {
                                if (e.key === "Enter" && e.ctrlKey) {
                                  e.preventDefault();
                                  handleUpdateComment(comment.id);
                                }
                                if (e.key === "Escape") {
                                  handleCancelEdit(comment.id);
                                }
                              }}
                              placeholder={t("feed_edit_comment_max", { limit: COMMENT_CHAR_LIMIT })}
                              className="w-full bg-transparent border-none outline-none text-base md:text-sm text-gray-900 placeholder-gray-500 resize-none min-h-[80px]"
                              disabled={isUpdatingComment[comment.id]}
                              rows={3}
                            />
                          </div>
                          {editErrors[comment.id] && (
                            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                              <p className="text-xs text-red-600 font-medium">{editErrors[comment.id]}</p>
                            </div>
                          )}
                          {editingCommentId === comment.id && (
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-gray-500">
                                {editCommentText[comment.id]?.length || 0} / {COMMENT_CHAR_LIMIT}
                              </span>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleCancelEdit(comment.id)}
                                  className="text-sm text-gray-600 hover:text-gray-800 font-medium px-3 py-1"
                                >
                                  {t("feed_cancel")}
                                </button>
                                <button
                                  onClick={() => handleUpdateComment(comment.id)}
                                  disabled={isUpdatingComment[comment.id] || ((editCommentText[comment.id]?.length || 0) > COMMENT_CHAR_LIMIT)}
                                  className="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
                                >
                                  {isUpdatingComment[comment.id] ? t("feed_updating") : t("feed_save")}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Reply Input */}
                    {replyingTo === comment.id && editingCommentId !== comment.id && (
                      <div className="mt-2 ml-1 flex items-start space-x-2">
                        <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                          {userInitial}
                        </div>
                        <div className="flex-1">
                          <div className="bg-gray-100 rounded-2xl px-3 py-2 focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                            <textarea
                              ref={(el) => {
                                replyInputRefs.current[comment.id] = el;
                              }}
                              value={replyText[comment.id] || ""}
                              onChange={(e) => {
                                setReplyText((prev) => ({
                                  ...prev,
                                  [comment.id]: e.target.value,
                                }));
                                setReplyErrors((prev) => ({ ...prev, [comment.id]: "" }));
                              }}
                              onKeyPress={(e) => {
                                if (e.key === "Enter" && e.ctrlKey) {
                                  e.preventDefault();
                                  handleReplySubmit(comment.id);
                                }
                              }}
                              placeholder={t("feed_reply_placeholder", { name: commentUserName, limit: COMMENT_CHAR_LIMIT })}
                              className="w-full bg-transparent border-none outline-none text-base md:text-sm text-gray-900 placeholder-gray-500 resize-none min-h-[60px]"
                              disabled={isReplying[comment.id]}
                              rows={2}
                            />
                          </div>
                          {replyErrors[comment.id] && (
                            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                              <p className="text-xs text-red-600 font-medium">{replyErrors[comment.id]}</p>
                            </div>
                          )}
                          {replyText[comment.id]?.trim() && (
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-gray-500">
                                {replyText[comment.id]?.length || 0} / {COMMENT_CHAR_LIMIT}
                              </span>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => {
                                    setReplyText((prev) => ({
                                      ...prev,
                                      [comment.id]: "",
                                    }));
                                    setReplyingTo(null);
                                    setReplyErrors((prev) => ({ ...prev, [comment.id]: "" }));
                                  }}
                                  className="text-sm text-gray-600 hover:text-gray-800 font-medium px-3 py-1"
                                >
                                  {t("feed_cancel")}
                                </button>
                                <button
                                  onClick={() => handleReplySubmit(comment.id)}
                                  disabled={isReplying[comment.id] || (replyText[comment.id]?.length || 0) > COMMENT_CHAR_LIMIT}
                                  className="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
                                >
                                  {isReplying[comment.id] ? t("feed_posting") : t("feed_reply")}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* View Replies Button */}
                    {comment.replies && comment.replies.length > 0 && !expandedComments.has(comment.id) && (
                      <button
                        onClick={() => {
                          setExpandedComments((prev) => {
                            const newSet = new Set(prev);
                            newSet.add(comment.id);
                            return newSet;
                          });
                        }}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium mt-1 ml-1 flex items-center space-x-1 max-lg:py-2 [@media(pointer:coarse)]:py-2"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        <span>{comment.replies.length === 1 
                          ? t("feed_view_reply_singular", { count: comment.replies.length })
                          : t("feed_view_replies_plural", { count: comment.replies.length })}</span>
                      </button>
                    )}

                    {/* Hide Replies Button */}
                    {comment.replies && comment.replies.length > 0 && expandedComments.has(comment.id) && (
                      <button
                        onClick={() => {
                          setExpandedComments((prev) => {
                            const newSet = new Set(prev);
                            newSet.delete(comment.id);
                            return newSet;
                          });
                        }}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium mt-1 ml-1 flex items-center space-x-1 max-lg:py-2 [@media(pointer:coarse)]:py-2"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 15l7-7 7 7"
                          />
                        </svg>
                        <span>{t("feed_hide_replies")}</span>
                      </button>
                    )}

                    {/* Nested Replies - Recursive */}
                    {comment.replies && comment.replies.length > 0 && expandedComments.has(comment.id) && renderNestedReplies(comment.replies)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center text-gray-500 text-sm py-4 mb-4">
          {t("feed_no_comments_yet")}
        </div>
      )}

      {/* Load More Comments Button */}
      {hasMoreComments && comments.length > 0 && (
        <div className="flex justify-center mt-4 mb-4">
          <button
            onClick={loadMoreComments}
            disabled={isLoadingMoreComments}
            className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoadingMoreComments ? (
              <span className="flex items-center space-x-2">
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>{t("feed_loading")}</span>
              </span>
            ) : (
              t("feed_load_more_comments")
            )}
          </button>
        </div>
      )}

      {/* Comment Input */}
      <div className="flex items-start space-x-2">
        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
          {userInitial}
        </div>
        <div className="flex-1">
          <div className="bg-gray-100 rounded-2xl px-3 py-2 focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-500 transition-all">
            <textarea
              ref={commentInputRef}
              value={commentText}
              onChange={(e) => {
                setCommentText(e.target.value);
                setCommentError("");
              }}
              onKeyPress={(e) => {
                if (e.key === "Enter" && e.ctrlKey) {
                  e.preventDefault();
                  handleCommentSubmit(e);
                }
              }}
              placeholder={t("feed_write_comment_max", { limit: COMMENT_CHAR_LIMIT })}
              className="w-full bg-transparent border-none outline-none text-base md:text-sm text-gray-900 placeholder-gray-500 resize-none min-h-[80px]"
              disabled={isCommenting}
              rows={3}
            />
          </div>
          {commentError && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-xs text-red-600 font-medium">{commentError}</p>
            </div>
          )}
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-gray-500">
              {commentText.length} / {COMMENT_CHAR_LIMIT}
            </span>
            {commentText.trim() && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    setCommentText("");
                    setCommentError("");
                  }}
                  className="text-sm text-gray-600 hover:text-gray-800 font-medium px-3 py-1"
                >
                  {t("feed_cancel")}
                </button>
                <button
                  onClick={handleCommentSubmit}
                  disabled={isCommenting || commentText.length > COMMENT_CHAR_LIMIT}
                  className="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
                >
                  {isCommenting ? t("feed_posting") : t("feed_post")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
